/**
 * Listas de precio y categorías de producto (bloque 13): los catálogos que hacen falta antes de cargar productos y
 * clientes reales. Solo la dueña los crea, los renombra y los activa o desactiva; nunca se borran. Queda en auditoría.
 */
import type { CategoriaDTO, ListaPrecioDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { BotonActivo } from '@/componentes/BotonActivo.tsx';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { get, mensajeDeError, patch, post } from '@/lib/api.ts';

/** «Lista 1» → «lista-1»: el código interno de la lista sale del nombre. */
const aCodigo = (nombre: string) => nombre.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);

/** `listasEditables`: las listas de precio valen para toda la empresa y las cambia solo la dueña (decisión del 2026-10-27). */
export function CatalogoCard({ listasEditables = true }: { listasEditables?: boolean }) {
  const qc = useQueryClient();
  const listas = useQuery({ queryKey: ['productos', 'listas', 'todas'], queryFn: () => get<ListaPrecioDTO[]>('/productos/listas-precio', { todas: 'true' }) });
  const categorias = useQuery({ queryKey: ['productos', 'categorias', 'todas'], queryFn: () => get<CategoriaDTO[]>('/productos/categorias/todas') });
  const [lista, setLista] = useState('');
  const [categoria, setCategoria] = useState('');
  const [padre, setPadre] = useState('');
  const refrescar = () => qc.invalidateQueries({ queryKey: ['productos'] });
  const alFallar = (e: unknown) => toast.error('No se pudo guardar', { description: mensajeDeError(e) });

  const crearLista = useMutation({
    mutationFn: () => post<ListaPrecioDTO>('/productos/listas-precio', { codigo: aCodigo(lista), nombre: lista.trim(), orden: (listas.data?.length ?? 0) + 1 }),
    onSuccess: (l) => { toast.success(`Lista «${l.nombre}» creada`, { description: 'Ya aparece en la ficha de cada producto y en la de cada cliente.' }); setLista(''); void refrescar(); },
    onError: alFallar,
  });
  const alternarLista = useMutation({
    mutationFn: ({ l, activa }: { l: ListaPrecioDTO; activa: boolean }) => patch<ListaPrecioDTO>(`/productos/listas-precio/${l.id}`, { activa }),
    onSuccess: (l) => { toast.success(`Lista «${l.nombre}» ${l.activa ? 'activada' : 'desactivada'}`); void refrescar(); },
    onError: alFallar,
  });
  const crearCategoria = useMutation({
    mutationFn: () => post<CategoriaDTO>('/productos/categorias', { nombre: categoria.trim(), padre_id: padre || null }),
    onSuccess: (c) => { toast.success(`${c.padre_id ? 'Subcategoría' : 'Categoría'} «${c.nombre}» creada`); setCategoria(''); void refrescar(); },
    onError: alFallar,
  });
  const alternarCategoria = useMutation({
    mutationFn: ({ c, activa }: { c: CategoriaDTO; activa: boolean }) => patch<CategoriaDTO>(`/productos/categorias/${c.id}`, { activa }),
    onSuccess: (c) => { toast.success(`«${c.nombre}» ${c.activa ? 'activada' : 'desactivada'}`); void refrescar(); },
    onError: alFallar,
  });

  return (
    <Card data-testid="catalogo">
      <CardHeader>
        <div className="flex items-center gap-1"><CardTitle>Listas de precio y categorías</CardTitle><AyudaVentana tema="configuracion.catalogo" /></div>
        <CardDescription>Cada producto lleva un precio por lista y cada cliente compra con la suya. Las categorías ordenan el catálogo y sirven para los descuentos programados. Nada se borra: lo que deja de usarse se desactiva.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-2">
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Listas de precio</h3>
          <EstadoQuery cargando={listas.isLoading} error={listas.error} datos={listas.data} reintentar={() => void listas.refetch()}>
            {(ls) => (
              <ul className="divide-y rounded-lg border" data-testid="cat-listas">
                {ls.map((l) => (
                  <li key={l.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm" data-testid={`cat-lista-${l.codigo}`}>
                    <span>
                      {l.nombre} {l.es_default ? <Badge variant="ok">la de un cliente nuevo</Badge> : null} {l.activa ? null : <Badge variant="neutro">desactivada</Badge>}
                    </span>
                    {l.es_default || !listasEditables ? null : (
                      <BotonActivo
                        activo={l.activa}
                        titulo={`Desactivar la lista «${l.nombre}»`}
                        consecuencia="La lista deja de ofrecerse en la ficha de los productos y de los clientes, y de ella no sale ningún precio; los precios que tiene guardados quedan igual. Una lista que tienen clientes activos no se desactiva: primero cámbiales la lista."
                        disabled={alternarLista.isPending}
                        alCambiar={(activa) => alternarLista.mutateAsync({ l, activa })}
                        testid={`cat-lista-alternar-${l.codigo}`}
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </EstadoQuery>
          {listasEditables ? (
            <div className="flex gap-2">
              <Input value={lista} onChange={(e) => setLista(e.target.value)} placeholder="Nombre de la lista (ej. Lista 1)" maxLength={60} aria-label="Nombre de la lista nueva" data-testid="cat-lista-nombre" />
              <Button disabled={aCodigo(lista).length < 2} cargando={crearLista.isPending} onClick={() => crearLista.mutate()} data-testid="cat-lista-agregar">Agregar</Button>
            </div>
          ) : <p className="text-xs text-muted-foreground" data-testid="cat-listas-solo-duena">Las listas de precio valen para toda la empresa: las crea y las desactiva solo la dueña.</p>}
        </section>
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">Categorías de producto</h3>
          <EstadoQuery cargando={categorias.isLoading} error={categorias.error} datos={categorias.data} reintentar={() => void categorias.refetch()}>
            {(cs) => {
              const raices = cs.filter((c) => !c.padre_id);
              const fila = (c: CategoriaDTO, sub: boolean) => (
                <li key={c.id} className={`flex items-center justify-between gap-2 py-1.5 pr-3 text-sm ${sub ? 'pl-8 text-muted-foreground' : 'pl-3 font-medium'}`} data-testid={`cat-categoria-${c.nombre}`}>
                  <span>{c.nombre} {c.activa ? null : <Badge variant="neutro">desactivada</Badge>}</span>
                  <BotonActivo
                    activo={c.activa}
                    titulo={`Desactivar la categoría «${c.nombre}»`}
                    consecuencia={`La categoría deja de ofrecerse para productos nuevos; los productos que ya la tienen la conservan.${sub ? '' : ' Se desactivan también sus subcategorías.'}`}
                    disabled={alternarCategoria.isPending}
                    alCambiar={(activa) => alternarCategoria.mutateAsync({ c, activa })}
                    testid={`cat-categoria-alternar-${c.nombre}`}
                  />
                </li>
              );
              return raices.length === 0 ? (
                <p className="rounded-lg border px-3 py-4 text-sm text-muted-foreground" data-testid="cat-sin-categorias">Todavía no hay categorías. Crea la primera aquí, o escríbelas en el Excel de productos y se crean solas.</p>
              ) : (
                <ul className="max-h-72 divide-y overflow-auto rounded-lg border" data-testid="cat-categorias">
                  {raices.flatMap((r) => [fila(r, false), ...cs.filter((c) => c.padre_id === r.id).map((c) => fila(c, true))])}
                </ul>
              );
            }}
          </EstadoQuery>
          <div className="flex flex-wrap gap-2">
            <Input value={categoria} onChange={(e) => setCategoria(e.target.value)} placeholder="Nombre de la categoría" maxLength={60} className="min-w-40 flex-1" aria-label="Nombre de la categoría nueva" data-testid="cat-categoria-nombre" />
            <Select value={padre} onChange={(e) => setPadre(e.target.value)} className="w-44" aria-label="Dentro de" data-testid="cat-categoria-padre">
              <option value="">Categoría principal</option>
              {(categorias.data ?? []).filter((c) => !c.padre_id && c.activa).map((c) => <option key={c.id} value={c.id}>Dentro de {c.nombre}</option>)}
            </Select>
            <Button disabled={categoria.trim().length < 2} cargando={crearCategoria.isPending} onClick={() => crearCategoria.mutate()} data-testid="cat-categoria-agregar">Agregar</Button>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
